import { useCallback } from 'react';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useInstallPermissions } from '../../contexts/InstallPermissionsContext';
import { useTeamsGate } from '../../contexts/TeamsGateContext';
import { filterGatedEntityTypes } from '../../utils/installPermissions';
import { useUnsavedChanges, useRegisterUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { useUpdateAllowedEntityTypeAndIds } from '../../hooks/mutations/useUpdateAllowedEntityTypeAndIds';
import { useApp } from '../../contexts/AppContext';
import { useInstallPermissionsSaveCallback } from '../../contexts/InstallPermissionsSaveCallbackContext';
import { useInstallPermissionsDowngrade } from '../../contexts/InstallPermissionsDowngradeContext';
import { useTracking } from '../../contexts/TrackingContext';
import { EVENTS, SCREENS, ACTIONS } from '../../constants/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function InstallPermissionsFooter() {
  const {
    appId,
    appName
  } = useApp();
  const {
    updatedUsersCount,
    updatedTeamsCount,
    hasChanges,
    installPermissionAccessState,
    getSelectedEntityTypeAndIds,
    commitAllUpdates,
    resetUpdatedUsers
  } = useInstallPermissions();
  const isUngatedForTeamsGate = useTeamsGate();
  const {
    isDialogShowing
  } = useUnsavedChanges();
  const {
    updateAllowedEntityTypeAndIds,
    loading,
    error
  } = useUpdateAllowedEntityTypeAndIds();
  const {
    onInstallPermissionsSave
  } = useInstallPermissionsSaveCallback();
  const {
    newInstallCount
  } = useInstallPermissionsDowngrade();
  const {
    track
  } = useTracking();
  const handleSaveChanges = useCallback(() => {
    const selectedEntityTypeAndIds = filterGatedEntityTypes(getSelectedEntityTypeAndIds(), isUngatedForTeamsGate);
    const isSpecificUsersAndTeams = installPermissionAccessState === 'SPECIFIC_USERS_AND_TEAMS';
    return updateAllowedEntityTypeAndIds({
      variables: Object.assign({
        appId,
        installPermissionAccessState
      }, isSpecificUsersAndTeams && {
        entityTypeAndIds: selectedEntityTypeAndIds
      })
    }).then(() => {
      track(EVENTS.PANEL_INTERACTED, {
        screen: SCREENS.MANAGEMENT_MODAL_PANEL,
        action: ACTIONS.INSTALL_PERMISSIONS_SAVED,
        actionValue: installPermissionAccessState,
        appId,
        appName
      });
      onInstallPermissionsSave === null || onInstallPermissionsSave === void 0 || onInstallPermissionsSave(installPermissionAccessState, newInstallCount);
      commitAllUpdates();
    });
  }, [getSelectedEntityTypeAndIds, isUngatedForTeamsGate, installPermissionAccessState, updateAllowedEntityTypeAndIds, appId, appName, onInstallPermissionsSave, newInstallCount, track, commitAllUpdates]);
  const hasUnsavedChanges = useCallback(() => hasChanges, [hasChanges]);
  useRegisterUnsavedChanges(hasUnsavedChanges, {
    onSave: handleSaveChanges,
    onDiscard: resetUpdatedUsers,
    loading,
    failed: !!error
  });
  return /*#__PURE__*/_jsx(AccordionTransition, {
    open: hasChanges,
    children: /*#__PURE__*/_jsx(PanelFooter, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          use: "primary",
          onClick: () => void handleSaveChanges(),
          loading: !isDialogShowing && loading,
          failed: !isDialogShowing && !!error,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.installPermissions.saveChanges.button"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: resetUpdatedUsers,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.installPermissions.discardChanges.button"
          })
        }), updatedUsersCount > 0 && /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-subtle",
          marginBlockEnd: "0",
          marginInlineStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.installPermissions.usersModified",
            options: {
              count: updatedUsersCount
            }
          })
        }), updatedTeamsCount > 0 && /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-subtle",
          marginBlockEnd: "0",
          marginInlineStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.installPermissions.teamsModified",
            options: {
              count: updatedTeamsCount
            }
          })
        })]
      })
    })
  });
}